import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useCurrentQueryRoute} from '@github-ui/react-core/future/use-current-query-route'
import {CopilotAnimation, CopilotAnimationType} from '@github-ui/copilot-animation'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {hpcAttrs} from '@github-ui/hpc'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import {useQueryClient} from '@github-ui/react-query'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {ContentView} from '@github-ui/pacer/ContentView'
import {Banner} from '@primer/react'
import {UploadIcon} from '@primer/octicons-react'
import {LazyGlobalSsoBanner} from '@github-ui/global-sso-banner-ui/lazy'
import type {SsoOrganization} from '@github-ui/global-sso-banner-ui/types'
import {useState, useEffect, useCallback, useMemo, useRef} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import styles from './CopilotAgentsPage.module.css'

import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {CurrentUserProvider} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {useInitializeAuthToken} from '@github-ui/agent-sessions/hooks/use-initialize-auth-token'
import {useSSOChangeDetection} from '@github-ui/agent-sessions/hooks/use-sso-change-detection'
import {useCreateAPITaskWithFailureBanner} from '@github-ui/agent-sessions/hooks/use-create-api-task-with-failure-banner'
import {TaskCreationFailedBannerList} from '@github-ui/agent-sessions/components/TaskCreationFailedBanner'
import {
  getAgentTasksQueryKeyForMutation,
  PAGE_SIZE,
  useInvalidateAgentTasks,
  useTasksCore,
} from '@github-ui/agent-sessions/hooks/useTasks'
import {useIcebreakers} from '@github-ui/agent-sessions/hooks/use-icebreakers'
import {useNewSessionEntrypoint} from '@github-ui/agent-sessions/hooks/use-new-session-entrypoint'
import {agentGroupedTasksQueryKey} from '@github-ui/agent-sessions/hooks/use-grouped-tasks-data'
import {AgentsBlankslate} from '@github-ui/agent-sessions/components/agents-view/AgentsBlankslate'
import {AgentsTaskList} from '@github-ui/agent-sessions/components/agents-view/AgentsTaskList'
import {AgentIcebreakers} from '@github-ui/copilot-shared-ui/components/AgentIcebreakers'
import {ShipFeedbackBanner} from '@github-ui/copilot-shared-ui/components/ShipFeedbackBanner'
import {TaskComposer} from '@github-ui/agent-sessions/components/new-task-chat-input/TaskComposer'
import {
  NewCopilotComposer,
  type ComposerHandle,
  type NewSessionConfiguration,
  type NewCopilotComposerTelemetry,
} from '@github-ui/one-copilot-composer/new'
import {FileAttachment} from '@github-ui/file-attachment'
import type {Attachment} from '@github-ui/file-attachment/attachment'
import {getActiveView} from '@github-ui/agent-sessions/components/agents-view/AgentsSidebar'
import {getCreatedByMeSearchParams} from '@github-ui/agent-sessions/components/agents-view/queries'
import {useRepoTasksFilter} from '@github-ui/agent-sessions/hooks/use-repo-tasks-filter'
import {seedRepoInfoCache} from '@github-ui/agent-sessions/hooks/use-repo-info'
import {setTitle} from '@github-ui/document-metadata'
import {AgentsSidebarLayout} from './AgentsSidebarLayout'
import {useEnvironmentTaskSubmit} from '@github-ui/agent-sessions/hooks/use-environment-task-submit'
import {useTaskEntrypoint} from '@github-ui/agent-sessions/hooks/useTaskEntrypoint'
import type {HandleUserSubmit, HandleUserSubmitOptions} from '@github-ui/one-copilot-composer/user-submit'
import type {CreateAPITaskParams} from '@github-ui/agent-sessions/types/api-task'
import {EntitlementProvider} from '@github-ui/copilot-entitlement/EntitlementContext'
import {getEnv} from '@github-ui/client-env'
import {CopilotLicenseType} from '@github-ui/copilot-chat-types'
import type {CopilotAgentsPayload} from './copilot-agents-route'
import {shouldShowSsoBanner} from '../utils/sso-banner-visibility'
import {setCopilotDocumentTitle} from '../utils/document-title'
import {isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {useAgentsHomeChat} from './use-agents-home-chat'
import {useChatSpaceParam} from './use-chat-space-param'
import {
  buildAgentsComposerConfiguration,
  toLegacyComposerProps,
  toNewComposerConfiguration,
} from './agents-composer-configuration'
import {
  renderComposerScopeOwnerPanel,
  renderComposerScopePicker,
} from '@github-ui/agent-sessions/components/new-task-chat-input/ScopePicker'
import {CLIModePicker} from '@github-ui/agent-sessions/components/task-view/CLIModePicker'
import {isQuickChatTaskViewEnabled} from '../utils/is-quick-chat-task'
import {launchCreationFailedTitle, usePersistentAgentsChat} from './PersistentAgentsChatHost'
import {TaskStartupTraceLink} from '@github-ui/agent-sessions/components/TaskStartupTraceLink'

// This composer only renders on the canonical /copilot route (the one_copilot_chat UI).
const COMPOSER_TELEMETRY: NewCopilotComposerTelemetry = {surface: 'immersive'}

type AgentsPageSelectedRepo = {nameWithOwner: string} | null
type AgentsPageSelectedAgent = {subAgentSlug?: string; topLevelAgentId?: string} | undefined

function getPageName() {
  return isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out') ? 'Copilot' : 'Agents'
}

export function buildAgentsPageTaskRequest({
  content,
  selectedRepo,
  selectedBranch,
  selectedAgent,
  modelId,
  options,
}: {
  content: string
  selectedRepo: AgentsPageSelectedRepo
  selectedBranch: string
  selectedAgent: AgentsPageSelectedAgent
  modelId?: string
  options?: HandleUserSubmitOptions
}): CreateAPITaskParams | null {
  const trimmedContent = content.trim()
  if (trimmedContent === '' || !selectedBranch || !selectedRepo) return null

  return {
    nwo: selectedRepo.nameWithOwner,
    problemStatement: trimmedContent,
    baseRef: selectedBranch,
    eventType: 'agents_page',
    eventContent: trimmedContent,
    model: modelId,
    customAgent: selectedAgent?.subAgentSlug,
    agentId: selectedAgent?.topLevelAgentId,
    mcpPermissionMode: options?.mcpPermissionMode,
    reasoningEffort: options?.reasoningEffort,
  }
}

export function CopilotAgentsPage() {
  const currentRoute = useCurrentQueryRoute()
  const {data: payload, queryKey, dataUpdatedAt} = useRouteQuery(currentRoute, 'mainQuery')
  const queryClient = useQueryClient()
  const oneCopilotEnabled = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')

  useEffect(() => {
    if (oneCopilotEnabled && payload) {
      setCopilotDocumentTitle(queryClient, queryKey)
    }
  }, [oneCopilotEnabled, payload, queryClient, queryKey, dataUpdatedAt])

  if (!payload) {
    return null
  }

  const typedPayload = payload as CopilotAgentsPayload

  return (
    <AppPayloadContext value={typedPayload}>
      <CurrentUserProvider currentUser={typedPayload.currentUser} currentUserIsSessionOwner>
        <ConsecutiveCAPI401sProvider>
          <ErrorBoundary boundaryName="CopilotAgentsPageContent">
            <CopilotAgentsPageContent payload={typedPayload} />
          </ErrorBoundary>
        </ConsecutiveCAPI401sProvider>
      </CurrentUserProvider>
    </AppPayloadContext>
  )
}

function CopilotAgentsPageContent({payload}: {payload: CopilotAgentsPayload}) {
  useInitializeAuthToken(payload.initialAuthToken)
  useSSOChangeDetection(payload.initialAuthToken?.ssoOrgIDs)
  const {disableSandboxSubmission, chatProviderError} = usePersistentAgentsChat()
  const chatApiURL = getEnv().copilotApiOverrideUrl
  const pageName = getPageName()

  // Prefill the repo-info cache with partial SSR repositories once, before the task list
  // renders, so repo-backed rows hydrate without a client repo-info fetch while detail consumers can refetch.
  const queryClient = useQueryClient()
  useState(() => {
    seedRepoInfoCache(queryClient, payload.repositories ?? [])
    return true
  })

  const ssoOrgs: SsoOrganization[] = payload.ssoOrganizations

  const [searchParams, setSearchParams] = useSearchParams()
  const view = getActiveView(searchParams, payload.currentUser?.login)

  const [totalTaskCount, setTotalTaskCount] = useState<number | undefined>(undefined)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const [prevView, setPrevView] = useState(view)
  const isNeedsAttentionView = view === 'needs_attention'

  // Reset stale task count state when navigating between views so the
  // previous view's count doesn't cause a banner flash during the transition.
  if (prevView !== view) {
    setPrevView(view)
    setTotalTaskCount(undefined)
    setHasActiveFilters(false)
  }

  const handleTotalTaskCount = useCallback((count: number | undefined, filtersActive: boolean) => {
    setTotalTaskCount(count)
    setHasActiveFilters(filtersActive)
  }, [])

  const {repoFilter, repoId, setRepoFilter} = useRepoTasksFilter()
  const newTaskInputRef = useRef<HTMLTextAreaElement | null>(null)
  const homeComposerRef = useRef<ComposerHandle>(null)
  const handleAcceptDroppedFiles = useCallback((attachments: Attachment[]) => {
    homeComposerRef.current?.addFiles(attachments.map(attachment => attachment.file))
  }, [])

  // Dedicated, user-scoped fetch for the "Needs attention" badge count.
  // Kept separate from the sidebar/content list fetches with stable params (no
  // status/sort/search/repo filters) so the query key never changes during tab or
  // filter navigation — this prevents the badge from flickering or disappearing.
  // Uses useTasksCore (no PR/repo hydration) since we only read the count, and
  // pageSize: 1 keeps the payload minimal (the count comes from the API's
  // `total_waiting_for_user_count` aggregate, which is independent of pagination),
  // scoped to the current user via creatorIds.
  const currentUserId = payload.currentUser?.id
  const {counts: needsAttentionCounts} = useTasksCore({
    taskFilter: 'active',
    includeCounts: true,
    pageSize: 1,
    creatorIds: currentUserId != null ? [currentUserId] : undefined,
    enabled: payload.agentsEnabled && currentUserId != null,
    scope: 'needs-attention-count',
  })
  const needsAttentionCount = needsAttentionCounts?.needsAttention

  // Owned at page level (not HomeView) so the handoff's failure banner survives the
  // immediate redirect to "My sessions", which unmounts HomeView.
  const invalidateAgentTasks = useInvalidateAgentTasks()
  const entrypointTaskConfig = useMemo(
    () => ({
      queryKey: getAgentTasksQueryKeyForMutation(),
      pageSize: PAGE_SIZE,
      // The optimistic write can't reach the user-scoped "My sessions" list, so
      // invalidate on success to refetch it now instead of on its next poll.
      onSuccess: invalidateAgentTasks,
    }),
    [invalidateAgentTasks],
  )
  const {createTask: createEntrypointTask, failureBannerProps: entrypointFailureBannerProps} =
    useCreateAPITaskWithFailureBanner(entrypointTaskConfig)

  const submitEntrypointTask = useCallback(
    (params: CreateAPITaskParams) => {
      // Redirect first, then create fire-and-forget; the destination shows a skeleton.
      setSearchParams(getCreatedByMeSearchParams())
      createEntrypointTask(params)
    },
    [setSearchParams, createEntrypointTask],
  )

  useEffect(() => {
    if (!isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')) {
      setTitle(`${pageName} · GitHub Copilot`)
    }
  }, [chatProviderError, disableSandboxSubmission, pageName])

  if (!payload.agentsEnabled) {
    return (
      <SidebarPageLayout
        storageKey="copilot-agents-sidebar"
        fitViewport="document"
        fillHeight
        className={styles.viewportLayout}
      >
        {/* as="div" avoids a duplicate <main> landmark — the dotcom Rails layout already wraps this React root in <main>. */}
        <SidebarPageLayout.Content as="div">
          <AgentsBlankslate
            hasCEorCBAccess={payload.hasCEorCBAccess}
            memberRequestEntities={payload.memberRequestEntities}
          />
        </SidebarPageLayout.Content>
      </SidebarPageLayout>
    )
  }

  const viewPageTitle = view === 'needs_attention' ? 'Needs attention' : view === 'created_by_me' ? 'My sessions' : null

  const isHomeView = view === 'home'
  const acceptsPageDrops = isHomeView && isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')

  const ssoBannerVisible = shouldShowSsoBanner({
    ssoOrgs,
    isNeedsAttentionView,
    needsAttentionCount,
    totalTaskCount,
    hasActiveFilters,
  })

  let content: React.ReactNode
  if (view === 'home') {
    content = (
      <HomeView
        payload={payload}
        textAreaRef={newTaskInputRef}
        composerHandleRef={homeComposerRef}
        onEntrypointSubmit={submitEntrypointTask}
      />
    )
  } else {
    content = (
      <AgentsTaskList
        repoFilter={repoFilter}
        repoId={repoId}
        setRepoFilter={setRepoFilter}
        isNeedsAttentionView={isNeedsAttentionView}
        initialTasks={payload.tasks}
        initialPullRequestsMap={payload.pullRequests}
        onTotalTaskCount={handleTotalTaskCount}
        trackHpc
        taskUrlScope="global"
      />
    )
  }

  const layout = (
    <AgentsSidebarLayout
      agentsEnabled={!!payload.agentsEnabled}
      enterpriseSandboxEnabled={payload.enterpriseSandboxEnabled !== false}
      showSpacesNav={payload.spacesEnabled}
      showCustomizeNav={payload.customizeEnabled}
      currentUserLogin={payload.currentUser?.login}
      currentUserId={payload.currentUser?.id}
      taskGroups={payload.taskGroups}
      contentKey={view}
      chatApiURL={chatApiURL}
      sidebarProps={{
        onNavigateHome: () => newTaskInputRef.current?.focus(),
        needsAttentionCount,
      }}
    >
      {/* as="div" avoids a duplicate <main> landmark — the dotcom Rails layout already wraps this React root in <main>. */}
      <SidebarPageLayout.Content as="div" className={isHomeView ? styles.homeContentPane : undefined}>
        <div className={`${styles.pageContainer}${viewPageTitle ? ` ${styles.pageContainerWithTitle}` : ''}`}>
          {isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out') && (
            <ShipFeedbackBanner treatment="private-preview" alignment="end" showEnvironmentStripTour={isHomeView} />
          )}
          <div
            className={`${styles.pageContent}${isHomeView ? ` ${styles.pageContentHome}` : ''}`}
            data-testid="copilot-agents-page-content"
          >
            {viewPageTitle ? (
              <div className={styles.pageHeader}>
                <SidebarPageLayout.SidebarTrigger />
                <h1 className={styles.pageTitle}>{viewPageTitle}</h1>
              </div>
            ) : (
              <SidebarPageLayout.SidebarTrigger />
            )}
            <TaskCreationFailedBannerList {...entrypointFailureBannerProps} className={styles.failedTaskBanners} />
            {content}
          </div>
        </div>
      </SidebarPageLayout.Content>
    </AgentsSidebarLayout>
  )

  return (
    // Full-width SSO banner above the layout so it spans the sidebar and content,
    // matching how Issues renders its SSO banner above the page layout.
    <>
      {ssoBannerVisible && <LazyGlobalSsoBanner ssoOrgs={ssoOrgs} />}
      {acceptsPageDrops ? (
        <FileAttachment
          className={styles.pageDropTarget}
          data-testid="agents-page-file-drop-target"
          inputId="agents-page-file-attachment-disabled"
          onAccept={handleAcceptDroppedFiles}
          preventAccidentalDrops
        >
          {layout}
          <div className={styles.pageDropOverlay} aria-hidden="true">
            <UploadIcon size={24} />
            <span>Drop files to attach</span>
          </div>
        </FileAttachment>
      ) : (
        layout
      )}
    </>
  )
}

// Exported for integration testing of the legacy-dashboard task handoff wiring
// (entrypoint consumption → task creation → post-success navigation).
export function HomeView({
  payload,
  textAreaRef,
  composerHandleRef,
  onEntrypointSubmit,
}: {
  payload: CopilotAgentsPayload
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  composerHandleRef?: React.RefObject<ComposerHandle | null>
  onEntrypointSubmit: (params: CreateAPITaskParams) => void
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const {space: spaceFromUrl, failed: spaceFromUrlFailed} = useChatSpaceParam()
  const hasAppliedInitialFocusRef = useRef(false)
  const agentsHomeChat = useAgentsHomeChat({payload, textAreaRef})
  const composerInitialInput = useMemo(() => {
    const initialInput = agentsHomeChat.composer.initialInput
    if (!spaceFromUrl) return initialInput

    const spaceContext = {kind: 'space', space: spaceFromUrl} as const
    if (!initialInput) return {target: {kind: 'chat' as const, context: spaceContext}}
    if (initialInput.target.kind !== 'chat') return initialInput

    return {...initialInput, target: {...initialInput.target, context: spaceContext}}
  }, [agentsHomeChat.composer.initialInput, spaceFromUrl])
  const handleSubmissionFailureDismiss = useCallback(() => {
    agentsHomeChat.clearSubmissionFailure()
    requestAnimationFrame(() => textAreaRef.current?.focus())
  }, [agentsHomeChat, textAreaRef])
  const taskListMutationConfig = useMemo(
    () => ({
      queryKey: getAgentTasksQueryKeyForMutation(),
      // Refresh the grouped sidebar cache too — it has a different shape and so
      // can't ride the flat optimistic path, but must reflect newly created tasks.
      additionalInvalidateKeys: [agentGroupedTasksQueryKey],
      pageSize: PAGE_SIZE,
      // Navigate on every successful creation — including the failure-banner retry,
      // which calls the mutation directly and so never re-enters handleUserSubmit.
      onSuccess: () => setSearchParams(getCreatedByMeSearchParams()),
    }),
    [setSearchParams],
  )

  const {createTaskAsync, failureBannerProps} = useCreateAPITaskWithFailureBanner(taskListMutationConfig)

  const composerConfiguration = useMemo(() => buildAgentsComposerConfiguration(payload), [payload])
  // The composer decides *whether* a scope can be picked; the page supplies the
  // control, so the org scope survives the move to the shared lifecycle exactly
  // as the legacy composer rendered it — including for a repoless Sandbox Chat.
  // Shared with the dashboard so the two hosts cannot drift apart.
  const renderScopePicker = useCallback<NonNullable<NewSessionConfiguration['scopePicker']>>(
    state => renderComposerScopePicker(state),
    [],
  )
  // The picker is the host's to render while the composer supplies the modes
  // valid for the selected destination.
  const {selectedMode, onSelectMode, selectedApprovalMode, onSelectApprovalMode} = agentsHomeChat.mode
  const newComposerConfiguration = useMemo(
    () => ({
      ...toNewComposerConfiguration(composerConfiguration, renderScopePicker, {
        scopeOwnerPanel: renderComposerScopeOwnerPanel,
        mode: selectedMode,
        onModeChange: selectedMode ? onSelectMode : undefined,
        approvalMode: selectedApprovalMode,
        onApprovalModeChange: selectedApprovalMode ? onSelectApprovalMode : undefined,
        disableSandboxSubmission: agentsHomeChat.disableSandboxSubmission,
        modePicker: selectedMode
          ? ({availableModes, approval}) => (
              <CLIModePicker
                selectedMode={selectedMode}
                onModeChange={onSelectMode}
                availableModes={availableModes}
                selectedApprovalMode={approval?.selectedMode}
                availableApprovalModes={approval?.availableModes}
                onApprovalModeChange={approval?.onModeChange}
              />
            )
          : undefined,
      }),
      directTurnTelemetrySurface: 'agents_page' as const,
    }),
    [
      agentsHomeChat.disableSandboxSubmission,
      composerConfiguration,
      onSelectApprovalMode,
      onSelectMode,
      renderScopePicker,
      selectedApprovalMode,
      selectedMode,
    ],
  )

  const {onEnvironmentSubmit, failedEnvironmentAttempts, handleEnvironmentDismiss} = useEnvironmentTaskSubmit({
    earlyProvisioning: agentsHomeChat.composer.earlyProvisioning,
    optimisticTaskListConfig: taskListMutationConfig,
    textAreaRef,
    taskUrlScope: 'global',
    chatSubmissionHandoff: agentsHomeChat.chatSubmissionHandoff,
    repositorySubmissionHandoff: agentsHomeChat.repositorySubmissionHandoff,
  })

  const {
    chatInputRef,
    visible: icebreakersVisible,
    handleSelect: handleIcebreakerSelect,
    handleDismiss: handleIcebreakerDismiss,
    handleIcebreakerSubmit,
  } = useIcebreakers({location: 'agents_page', variant: payload.icebreakerVariant, textAreaRef})

  useEffect(() => {
    if (hasAppliedInitialFocusRef.current) return

    const textArea = textAreaRef.current
    if (!textArea) return

    hasAppliedInitialFocusRef.current = true
    textArea.focus()
    // The composer is withheld while a One Copilot request is claimed, so the
    // textarea only exists after that resolves and this has to run again.
  }, [textAreaRef, agentsHomeChat.isComposerWithheld, agentsHomeChat.composer.key])
  useEffect(() => {
    if (!agentsHomeChat.submissionFailure) return
    if (document.activeElement !== document.body) return
    requestAnimationFrame(() => {
      if (document.activeElement !== document.body) return
      textAreaRef.current?.focus()
    })
  }, [agentsHomeChat.submissionFailure, textAreaRef])
  useNewSessionEntrypoint(textAreaRef)

  const handleUserSubmit = useCallback(
    async (
      content: string,
      selectedRepo: {nameWithOwner: string} | null,
      selectedBranch: string,
      selectedAgent: {subAgentSlug?: string; topLevelAgentId?: string} | undefined,
      modelId?: string,
      options?: HandleUserSubmitOptions,
    ) => {
      handleIcebreakerSubmit(content)
      const taskRequest = buildAgentsPageTaskRequest({
        content,
        selectedRepo,
        selectedBranch,
        selectedAgent,
        modelId,
        options,
      })
      if (!taskRequest) return

      await createTaskAsync(taskRequest)
    },
    [createTaskAsync, handleIcebreakerSubmit],
  )

  // Auto-kick off a task handed off from the legacy github.com dashboard task input,
  // which stashes the prompt (repo/branch/agent) in local storage and hard-navigates
  // here. Without this the redesigned page would drop the task on the floor.
  //
  // The handoff redirects immediately and creates in the background (see
  // submitEntrypointTask), unlike a direct in-page submission (handleUserSubmit).
  const handleEntrypointSubmit = useCallback<HandleUserSubmit>(
    (content, selectedRepo, selectedBranch, selectedAgent, modelId, options) => {
      handleIcebreakerSubmit(content)
      const taskRequest = buildAgentsPageTaskRequest({
        content,
        selectedRepo,
        selectedBranch,
        selectedAgent,
        modelId,
        options,
      })
      if (!taskRequest) return

      onEntrypointSubmit(taskRequest)
    },
    [onEntrypointSubmit, handleIcebreakerSubmit],
  )
  useTaskEntrypoint(handleEntrypointSubmit)

  const initialLicenseType = payload.hasPaidCopilotAccess
    ? CopilotLicenseType.LicensedFull
    : CopilotLicenseType.LicensedLimited
  return (
    <EntitlementProvider initialLicenseType={initialLicenseType} hasCEorCBAccess={payload.hasCEorCBAccess}>
      <ContentView.Container width="small">
        <h1 className="sr-only">{getPageName()}</h1>
        <div className={styles.homeContent}>
          <ContentView.Header>
            <div className={styles.pageIcon}>
              <CopilotAnimation animationType={CopilotAnimationType.Idle} loopAnimation size={64} />
            </div>
          </ContentView.Header>
          <ContentView.Section>
            {agentsHomeChat.entryFailure ? (
              <Banner
                variant="critical"
                title="Copilot request failed"
                description={agentsHomeChat.entryFailure}
                secondaryAction={
                  <Banner.SecondaryAction onClick={agentsHomeChat.dismissEntryFailure}>Dismiss</Banner.SecondaryAction>
                }
              />
            ) : null}
            {agentsHomeChat.chatProviderError ? (
              <Banner
                variant="critical"
                title="Unable to load Copilot Chat"
                description={agentsHomeChat.chatProviderError.message}
              />
            ) : null}
            {spaceFromUrlFailed ? (
              <Banner
                variant="warning"
                title="Space not attached"
                description="We couldn't attach that space to this chat. You can attach it from the attachments menu."
              />
            ) : null}
            <div {...hpcAttrs()}>
              {agentsHomeChat.usesOneCopilotComposer ? (
                agentsHomeChat.isComposerWithheld ? (
                  // The composer snapshots its initial input on mount, so it is
                  // withheld until the stored request resolves rather than
                  // mounted empty and updated after the fact.
                  <div aria-busy={agentsHomeChat.isClaimingEntry || undefined} data-testid="agents-composer-restoring">
                    <span className="sr-only">
                      {agentsHomeChat.isClaimingEntry
                        ? 'Restoring your Copilot request…'
                        : 'Dismiss the request failure to continue.'}
                    </span>
                  </div>
                ) : (
                  <NewCopilotComposer
                    key={`${agentsHomeChat.composer.key}${spaceFromUrl ? `-space-${spaceFromUrl.owner}/${spaceFromUrl.id}` : ''}`}
                    controller={{
                      earlyProvisioning: agentsHomeChat.composer.earlyProvisioning,
                      createTask: handleUserSubmit,
                      createEnvironmentTask: onEnvironmentSubmit,
                      chatLaunch: agentsHomeChat.composer.chatLaunch,
                    }}
                    initialInput={composerInitialInput}
                    draftSource="agents_page"
                    telemetry={COMPOSER_TELEMETRY}
                    configuration={newComposerConfiguration}
                    presentation={{
                      showEnvironmentStripTour: true,
                      chatInputRef,
                      textAreaRef,
                      handleRef: composerHandleRef,
                      clearOnSubmit: composerConfiguration.clearOnSubmit,
                      disableWhileSubmitting: composerConfiguration.disableWhileSubmitting,
                    }}
                  />
                )
              ) : (
                <TaskComposer
                  key={spaceFromUrl ? `space-${spaceFromUrl.owner}/${spaceFromUrl.id}` : undefined}
                  handleUserSubmit={handleUserSubmit}
                  eventType="agents_page"
                  directTurnTelemetrySurface="agents_page"
                  chatInputRef={chatInputRef}
                  textAreaRef={textAreaRef}
                  onEnvironmentSubmit={onEnvironmentSubmit}
                  {...agentsHomeChat.legacy}
                  {...toLegacyComposerProps(composerConfiguration)}
                  initialSpace={spaceFromUrl}
                  disableSandboxSubmission={agentsHomeChat.disableSandboxSubmission}
                  enableQuickChatDraftRecovery={isQuickChatTaskViewEnabled()}
                  initialText={
                    agentsHomeChat.submissionFailure?.text ??
                    agentsHomeChat.recoveryPrompt ??
                    searchParams.get('prompt') ??
                    undefined
                  }
                />
              )}
            </div>
            {agentsHomeChat.submissionFailure && (
              <Banner
                variant="critical"
                title={launchCreationFailedTitle(agentsHomeChat.submissionFailure.launchTarget)}
                description={
                  <>
                    {agentsHomeChat.submissionFailure.message}
                    <TaskStartupTraceLink diagnosticId={agentsHomeChat.submissionFailure.clientGeneratedId} />
                  </>
                }
                onDismiss={handleSubmissionFailureDismiss}
              />
            )}
            <TaskCreationFailedBannerList {...failureBannerProps} className={styles.failedTaskBanners} />
            <TaskCreationFailedBannerList
              attempts={failedEnvironmentAttempts}
              onDismiss={handleEnvironmentDismiss}
              className={styles.failedTaskBanners}
            />
          </ContentView.Section>
          {icebreakersVisible && (
            <ContentView.Section>
              <AgentIcebreakers
                onSelect={handleIcebreakerSelect}
                onDismiss={handleIcebreakerDismiss}
                location="agents_page"
                variant={payload.icebreakerVariant}
                headingLevel="h2"
              />
              <p className={styles.aiDisclaimer}>Copilot can make mistakes. Check important info.</p>
            </ContentView.Section>
          )}
        </div>
      </ContentView.Container>
    </EntitlementProvider>
  )
}
